import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, CheckCircle2, ClipboardList } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const Field = ({ q, value, onChange }) => {
  if (q.type === "textarea") return <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} data-testid={`pub-answer-${q.id}`} className="eyw-input w-full resize-none" />;
  if (q.type === "select") return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={`pub-answer-${q.id}`} className="eyw-input w-full">
      <option value="">Select…</option>
      {(q.options || []).filter(Boolean).map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (q.type === "checkboxes") {
    const cur = (value || "").split(", ").filter(Boolean);
    return (
      <div className="flex flex-wrap gap-2">
        {(q.options || []).filter(Boolean).map((o) => {
          const on = cur.includes(o);
          return <button key={o} type="button" onClick={() => onChange((on ? cur.filter((x) => x !== o) : [...cur, o]).join(", "))}
            className={`px-4 py-2 rounded-full border text-sm ${on ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>{o}</button>;
        })}
      </div>
    );
  }
  if (q.type === "yes_no") return (
    <div className="flex gap-2">{["Yes", "No"].map((o) => <button key={o} type="button" onClick={() => onChange(o)}
      className={`px-5 py-2 rounded-full border text-sm ${value === o ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>{o}</button>)}</div>
  );
  if (q.type === "scale") return (
    <div className="flex gap-2">{[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" onClick={() => onChange(String(n))}
      className={`h-10 w-10 rounded-full border text-sm font-medium ${value === String(n) ? "bg-[#D98E73] text-white border-[#D98E73]" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>{n}</button>)}</div>
  );
  return <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={`pub-answer-${q.id}`} className="eyw-input w-full" />;
};

export default function PublicFormPage() {
  const { token } = useParams();
  const { user } = useAuth();
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const { data: form, isLoading, error } = useQuery({
    queryKey: ["public-form", token],
    queryFn: () => api.get(`/public/forms/${token}`).then((r) => r.data),
    retry: false,
  });

  const submit = async () => {
    setSending(true);
    try {
      await api.post(`/public/forms/${token}`, {
        answers: form.questions.map((q) => ({ id: q.id, label: q.label, answer: answers[q.id] || "" })),
        name, email,
      });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (isLoading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-[#144F75]" size={30} /></div>;
  if (error || !form) return (
    <div className="min-h-[60vh] grid place-items-center px-6" data-testid="public-form-invalid">
      <p className="font-serif text-2xl text-[#1A242B]">This form link isn't valid or is no longer active.</p>
    </div>
  );

  return (
    <div className="max-w-xl mx-auto px-6 pt-14 pb-36" data-testid="public-form-page">
      {done ? (
        <div className="eyw-card p-8 text-center" data-testid="public-form-done">
          <CheckCircle2 size={44} className="text-[#5C8A72] mx-auto mb-3" />
          <h1 className="font-serif text-2xl text-[#1A242B]">Thank you!</h1>
          <p className="text-[#4A5D6B] mt-2 text-sm">Your answers were sent securely{form.owner_name ? ` to ${form.owner_name}` : ""}.</p>
          <p className="mt-6"><Link to="/" className="text-sm text-[#144F75] hover:underline">Explore Enhancing Your World →</Link></p>
        </div>
      ) : (
        <div className="eyw-card p-7 md:p-9">
          <p className="eyw-label mb-1 flex items-center gap-1.5"><ClipboardList size={13} /> {form.owner_name || "Enhancing Your World"}</p>
          <h1 className="font-serif text-2xl md:text-3xl text-[#1A242B]">{form.title}</h1>
          {form.description && <p className="text-sm text-[#4A5D6B] mt-2">{form.description}</p>}
          <div className="space-y-4 mt-6">
            {form.questions.map((q) => (
              <div key={q.id}>
                <label className="text-sm text-[#1A242B] block mb-1">{q.label}{q.required && <span className="text-[#D98E73]"> *</span>}</label>
                <Field q={q} value={answers[q.id] || ""} onChange={(v) => setAnswers({ ...answers, [q.id]: v })} />
              </div>
            ))}
            {!user && (
              <div className="grid sm:grid-cols-2 gap-3 border-t border-[#E5E0D8] pt-4">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name *" data-testid="pub-form-name" className="eyw-input" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email *" data-testid="pub-form-email" className="eyw-input" />
              </div>
            )}
            <button onClick={submit} disabled={sending} data-testid="pub-form-submit" className="eyw-btn w-full justify-center disabled:opacity-60">{sending && <Loader2 className="animate-spin" size={16} />} Send my answers</button>
          </div>
        </div>
      )}
    </div>
  );
}
